import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import {
  createChatSidebarSlice,
  type ChatSidebarSlice,
} from './chatSidebarSlice'
import { createTabsSlice, type TabsSlice } from './tabsSlice'

export type { WorkspaceTab, CloseTabResult } from './tabsSlice'
export type { ChatSidebar } from './chatSidebarSlice'

export type AppStore = ChatSidebarSlice & TabsSlice

const STORAGE_KEY = 'hoku:app'

/**
 * Single source of truth for app-shell UI state — chat sidebar + main panel
 * tabs. Consumers should read tabs via `useAppTabs()` for the public surface;
 * the bridge (`AppShellEffects`) writes URL-driven actions directly via
 * `useAppStore.getState()`. Only `chatSidebar` persists to localStorage.
 *
 * `version` + `migrate` are set so future milestones that change the
 * persisted shape can route returning users through a real migration path.
 * Today's `migrate` is a no-op; bump the version and add a real transform
 * when a persisted field changes type/shape.
 */
export const useAppStore = create<AppStore>()(
  persist(
    (...a) => ({
      ...createChatSidebarSlice(...a),
      ...createTabsSlice(...a),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ chatSidebar: state.chatSidebar }),
      version: 0,
      migrate: (persistedState) => persistedState,
    }
  )
)

let storageSyncRegistered = false

/**
 * Wire cross-tab storage rehydration. Idempotent — the
 * `storageSyncRegistered` flag prevents double-attach across mounts
 * (StrictMode dev double-render, hot reload). Must be called from a
 * mount lifecycle (typically `<AppShellEffects />`'s `useEffect`),
 * NOT at module load — running at import time fires `addEventListener`
 * on every test that swaps a fresh JSDOM window.
 */
export function registerStorageSync() {
  if (storageSyncRegistered || typeof window === 'undefined') return
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) return
    void useAppStore.persist.rehydrate()
  })
  storageSyncRegistered = true
}
